import { useEffect, useRef, useState } from 'react';
import { useAppApi } from '@/lib/api';
import { turnFlagsFor } from './turnFlags';

const POLL_INTERVAL_MS = 1500;
const sessionStorageKey = (agentKey) => `agentChatSession:${agentKey}`;

/** The assistant message a finished turn hung its tool calls off — the id
 *  GET /session/:id/traces needs to reveal what those tools actually returned. */
const lastToolMessage = (messages = []) => [...messages].reverse()
  .find((row) => row.role === 'assistant' && (row.toolCallCount || 0) > 0);

/** Keeps the latest value in a ref, so a caller can pass an inline callback without
 *  memoizing it just to avoid re-running this hook's effects. */
function useLatest(value) {
  const ref = useRef(value);
  ref.current = value;
  return ref;
}

/** The client-side view of one persisted message row. */
const toMessage = (row) => ({
  id: row.id, role: row.role, content: row.content,
  toolCallCount: row.toolCallCount || 0, file: row.file || null,
});

/**
 * Shared chat state/logic behind both the floating dock (AgentChatDock.jsx) and the
 * full-page Chat.jsx — same agent picker + session-per-agent + send() behavior either
 * surface can render however it likes. Avoids duplicating this logic in two places.
 *
 * Async (PRD's "don't make the user wait in the browser" requirement, services/chat/
 * chatEngine.js's module comment): send() only submits a turn and returns immediately
 * — the actual LLM/tool-calling loop runs server-side (chatJobWorker.js). This hook
 * polls GET /api/agent/session/:id/status for the result, and — since each agent's
 * session id is persisted to localStorage, not just kept in memory — recovers that
 * same "in progress" or "done" state after a full page reload or a closed/reopened
 * tab, not only within one still-open browser session.
 */
export function useAgentChat({ getContext, onTurnComplete } = {}) {
  const { fetchApi } = useAppApi();
  const [agents, setAgents] = useState([]);
  const [agentKey, setAgentKeyState] = useState('');
  const [sessionsByAgent, setSessionsByAgent] = useState({});
  const [messagesByAgent, setMessagesByAgent] = useState({});
  const [pendingByAgent, setPendingByAgent] = useState({}); // agentKey -> pendingJob {id, status, traces} | null
  const [loadError, setLoadError] = useState('');
  const [approvalMode, setApprovalMode] = useState('attended'); // HITL-TOOLING.md §1.3
  const loadedRef = useRef(false);
  const pollTimerRef = useRef(null);
  const getContextRef = useLatest(getContext);
  const onTurnCompleteRef = useLatest(onTurnComplete);
  const wasPendingRef = useRef(false); // was a turn in flight on the previous refresh?

  /** Surfaces a failure as a message in the thread rather than a silent no-op. */
  const appendError = (forAgentKey, err) => {
    const msg = (err.response && err.response.data && err.response.data.error) || err.message || 'Something went wrong.';
    setMessagesByAgent((m) => ({ ...m, [forAgentKey]: [...(m[forAgentKey] || []), { role: 'error', content: msg }] }));
  };

  const loadAgents = () => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    fetchApi('agentList')
      .then((data) => {
        setAgents(data.agents || []);
        if (data.agents && data.agents.length) setAgentKeyState((k) => k || data.agents[0].key);
      })
      .catch((err) => setLoadError((err.response && err.response.data && err.response.data.error) || err.message));
  };

  const stopPolling = () => {
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    pollTimerRef.current = null;
  };

  /** Reads current DB state for a session and updates that agent's messages/pending
   *  flag — the single source of truth both the initial load and every poll tick use.
   *  Each message carries toolCallCount (0 = no trace badge) and, while a turn is
   *  in-flight, pendingJob.traces gives live "currently running: X" visibility
   *  (services/chat/chatEngine.js's startTrace()/finishTrace()) without a second poll. */
  const refreshSession = async (forAgentKey, sessionId) => {
    const data = await fetchApi('agentSessionStatus', sessionId);
    setMessagesByAgent((m) => ({ ...m, [forAgentKey]: data.messages.map(toMessage) }));
    setPendingByAgent((p) => ({ ...p, [forAgentKey]: data.pendingJob || null }));

    // The pending → not-pending edge is the only moment a turn is known to have finished;
    // polling simply stops otherwise. Tool results live only in the traces endpoint, so
    // the caller gets the message id it needs to go and fetch them.
    const pending = !!data.pendingJob;
    const landed = wasPendingRef.current && !pending;
    wasPendingRef.current = pending;
    if (landed && onTurnCompleteRef.current) {
      const last = lastToolMessage(data.messages);
      if (last) onTurnCompleteRef.current({ sessionId, messageId: last.id, agentKey: forAgentKey });
    }
    return pending;
  };

  const pollUntilDone = (forAgentKey, sessionId) => {
    stopPolling();
    const tick = async () => {
      let stillPending = true;
      try {
        stillPending = await refreshSession(forAgentKey, sessionId);
      } catch {
        // transient — keep polling rather than giving up on one failed tick
      }
      if (stillPending) pollTimerRef.current = setTimeout(tick, POLL_INTERVAL_MS);
    };
    pollTimerRef.current = setTimeout(tick, POLL_INTERVAL_MS);
  };

  // On (re)selecting an agent, recover its persisted session — this is what makes a
  // reload/reopen show the finished answer or a live "in progress" state instead of a
  // blank thread.
  useEffect(() => {
    stopPolling();
    if (!agentKey) return;
    const savedSessionId = localStorage.getItem(sessionStorageKey(agentKey));
    if (!savedSessionId) return;
    setSessionsByAgent((s) => ({ ...s, [agentKey]: savedSessionId }));
    refreshSession(agentKey, savedSessionId)
      .then((isPending) => { if (isPending) pollUntilDone(agentKey, savedSessionId); })
      .catch(() => {}); // no history yet, or a transient error — start fresh silently
    return stopPolling;
  }, [agentKey]);

  const setAgentKey = (key) => setAgentKeyState(key);

  /** Uploads a composer attachment (attach button or paste-image, ChatPanel.jsx)
   *  ahead of send() — resolves/creates the session server-side the same way send()
   *  does, so attaching a file as the very first action in a brand-new thread still
   *  works even though no sessionId exists client-side yet. Returns {fileId, filename,
   *  mimeType, preview} for the composer to show a pending chip; send() takes the
   *  fileId later to link it to the outgoing message. */
  const uploadAttachment = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('agentKey', agentKey);
    if (sessionsByAgent[agentKey]) formData.append('sessionId', sessionsByAgent[agentKey]);
    const data = await fetchApi('agentUploadAttachment', formData);
    setSessionsByAgent((s) => ({ ...s, [agentKey]: data.sessionId }));
    localStorage.setItem(sessionStorageKey(agentKey), String(data.sessionId));
    return { fileId: data.fileId, filename: data.filename, mimeType: data.mimeType, preview: data.preview };
  };

  /** `attachment` (optional): {fileId, filename, mimeType, preview} from
   *  uploadAttachment() — shown on the optimistic user bubble immediately (using its
   *  own preview data, not yet the server-joined file), and its fileId is sent as
   *  attachmentFileId so submitTurn() can link it to this turn's user message. */
  const send = async (text, attachment, turnOpts) => {
    const trimmed = text.trim();
    if ((!trimmed && !attachment) || pendingByAgent[agentKey] || !agentKey) return;
    const optimisticFile = attachment ? { id: attachment.fileId, filename: attachment.filename, mimeType: attachment.mimeType, preview: attachment.preview } : null;
    setMessagesByAgent((m) => ({ ...m, [agentKey]: [...(m[agentKey] || []), { role: 'user', content: trimmed, file: optimisticFile }] }));
    setPendingByAgent((p) => ({ ...p, [agentKey]: { id: null, status: 'queued', traces: [] } }));
    try {
      const data = await fetchApi('agentChat', {
        agentKey, message: trimmed, sessionId: sessionsByAgent[agentKey], approvalMode,
        attachmentFileId: attachment ? attachment.fileId : undefined,
        // Read at send time, not render time, so the agent always sees the CURRENT screen
        // rather than whatever it looked like when the dock last rendered.
        context: getContextRef.current ? getContextRef.current() : undefined,
        // Per-turn capability overrides (voice commands: retrieval off, tools off)
        ...turnFlagsFor(turnOpts),
      });
      setSessionsByAgent((s) => ({ ...s, [agentKey]: data.sessionId }));
      localStorage.setItem(sessionStorageKey(agentKey), String(data.sessionId));
      pollUntilDone(agentKey, data.sessionId);
    } catch (err) {
      appendError(agentKey, err);
      setPendingByAgent((p) => ({ ...p, [agentKey]: null }));
    }
  };

  /** Abandons the current agent's session client-side only — no backend call, no data
   *  deleted. The next send()/uploadAttachment() starts a brand-new session, same as a
   *  ChatGPT-style "New chat"; the old thread stays in the DB, just no longer
   *  referenced by this UI (matches this app's "no destructive action without an
   *  explicit user gesture" pattern — the confirm happens in ChatPanel.jsx before this
   *  is even called). */
  const clearChat = () => {
    if (!agentKey) return;
    stopPolling();
    localStorage.removeItem(sessionStorageKey(agentKey));
    setSessionsByAgent((s) => ({ ...s, [agentKey]: undefined }));
    setMessagesByAgent((m) => ({ ...m, [agentKey]: [] }));
    setPendingByAgent((p) => ({ ...p, [agentKey]: null }));
  };

  /** Answers a paused ask_user question (services/chat/tools/index.js) — resumes the
   *  same turn server-side; polling picks the result back up exactly like a fresh send(). */
  const answerQuestion = async ({ selectedValue, freeText }) => {
    const pending = pendingByAgent[agentKey];
    const sessionId = sessionsByAgent[agentKey];
    if (!pending || !pending.id || !sessionId) return;
    try {
      await fetchApi('agentAnswerQuestion', { sessionId, jobId: pending.id, selectedValue, freeText });
      pollUntilDone(agentKey, sessionId);
    } catch (err) {
      appendError(agentKey, err);
    }
  };

  return {
    agents, agentKey, setAgentKey, loadAgents,
    sessionId: sessionsByAgent[agentKey] || null,
    messages: messagesByAgent[agentKey] || [],
    sending: !!pendingByAgent[agentKey],
    pendingJob: pendingByAgent[agentKey] || null,
    loadError, send, answerQuestion, uploadAttachment, clearChat,
    approvalMode, setApprovalMode,
    currentAgent: agents.find((a) => a.key === agentKey),
  };
}

export default useAgentChat;
